import * as React from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { cn } from '@core/lib/utils';
import { Button } from '@core/components/ui/shared/buttons/button';
import { Input } from '@core/components/ui/shared/inputs/input';
import Label from '@core/components/ui/shared/labels/label';

export type PasswordInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
};

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, label, placeholder, id, value, onChange, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const [showPassword, setShowPassword] = React.useState(false);

    return (
      <div className={cn('relative group mt-2', className)}>
        <Input
          type={showPassword ? 'text' : 'password'}
          placeholder=""
          className={cn('peer border-border h-10 pr-10', className)}
          ref={ref}
          id={inputId}
          value={value ?? ''}
          onChange={onChange}
          {...props}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground transition-opacity opacity-0 group-hover:opacity-100"
          onClick={() => setShowPassword((prev) => !prev)}
        >
          {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
        </Button>
        <Label
          htmlFor={inputId}
          className={cn(
            'absolute left-3 top-0 z-10 origin-left -translate-y-1/2 scale-75 transform bg-background px-1 text-sm text-muted-foreground transition-all duration-200',
            'peer-placeholder-shown:top-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-muted-foreground',
            'peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:bg-background peer-focus:px-1 peer-focus:text-primary',
            'peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:bg-background peer-[:not(:placeholder-shown)]:px-1',
            'cursor-text pointer-events-none select-none'
          )}
        >
          {label || placeholder}
        </Label>
      </div>
    );
  }
);
PasswordInput.displayName = 'PasswordInput';

export { PasswordInput };